/**
 * League Types for Web Frontend
 * Mirrors backend models from @devour/models
 */

/*
 * Enums re-exported from the generated client so LeagueResponse nominal
 * identity holds and consumers stop casting API results to local shadows.
 * (Backend is the source of truth: LeagueTeamStatus has ACTIVE/DISQUALIFIED,
 * not the web-only REJECTED; MatchFormat adds BEST_OF_7/FIRST_TO_X.)
 */
import {
    LeagueStatus,
    RoundRobinType,
    MatchFormat,
    LeagueTeamStatus,
    CompetitionMatchStatus,
} from "@devour/client";
import type { LeagueResponse } from "@devour/client";

export {
    LeagueStatus,
    RoundRobinType,
    MatchScoringMode,
    MatchFormat,
    LeagueTeamStatus,
} from "@devour/client";

// Display labels for UI
export const LEAGUE_STATUS_LABELS: Record<LeagueStatus, string> = {
    [LeagueStatus.DRAFT]: "Coming Soon",
    [LeagueStatus.REGISTRATION]: "Open for Registration",
    [LeagueStatus.ACTIVE]: "In Progress",
    [LeagueStatus.PLAYOFFS]: "Playoffs",
    [LeagueStatus.COMPLETED]: "Completed",
    [LeagueStatus.ARCHIVED]: "Archived",
};

export const ROUND_ROBIN_TYPE_LABELS: Record<RoundRobinType, string> = {
    [RoundRobinType.SINGLE]: "Single Round-Robin",
    [RoundRobinType.DOUBLE]: "Double Round-Robin",
};

export const MATCH_FORMAT_LABELS: Record<MatchFormat, string> = {
    [MatchFormat.SINGLE_GAME]: "Single Game",
    [MatchFormat.BEST_OF_3]: "Best of 3",
    [MatchFormat.BEST_OF_5]: "Best of 5",
    [MatchFormat.BEST_OF_7]: "Best of 7",
    [MatchFormat.FIRST_TO_X]: "First to X",
};

// Points configuration
export interface LeaguePointsConfig {
    pointsPerWin: number;
    pointsPerDraw: number;
    pointsPerLoss: number;
}

// League standing entry
export interface LeagueStanding {
    position: number;
    teamId: string;
    teamName: string;
    tag?: string;
    played: number;
    wins: number;
    draws: number;
    losses: number;
    goalsFor: number;
    goalsAgainst: number;
    goalDifference: number;
    points: number;
    form?: string[];
}

// Matchday structure
export interface LeagueMatchday {
    matchdayNumber: number;
    name: string;
    fixtureIds: string[];
    scheduledDate?: number;
    isComplete: boolean;
}

// Roster member in a team
export interface LeagueRosterMember {
    odId: string;
    displayName: string;
    role: "CAPTAIN" | "PLAYER" | "SUBSTITUTE";
    joinedAt: number;
}

// Team stats
export interface LeagueTeamStats {
    played: number;
    wins: number;
    draws: number;
    losses: number;
    goalsFor: number;
    goalsAgainst: number;
    goalDifference: number;
    points: number;
    form: string[];
}

/*
 * Main League interface
 * The user-facing league type IS the generated client response — no local
 * shadow. LeagueResponse is a superset of the former shadow (adds divisions/
 * tiebreaker/playoff config) with generated enum types.
 */
export type League = LeagueResponse;

// League Team interface
export interface LeagueTeam {
    _id: string;
    id: string;
    leagueId: string;
    name: string;
    tag?: string;
    captainOdId: string;
    roster: LeagueRosterMember[];
    position: number;
    stats: LeagueTeamStats;
    status: LeagueTeamStatus;
    registeredAt: number;
}

// API Request/Response types
export interface GetLeaguesParams {
    limit?: number;
    offset?: number;
    status?: LeagueStatus | LeagueStatus[];
    gameId?: string;
    search?: string;
}

export interface GetLeaguesResponse {
    leagues: League[];
    total: number;
    limit: number;
    offset: number;
}

export interface GetLeagueTeamsResponse {
    teams: LeagueTeam[];
    total: number;
    limit: number;
    offset: number;
}

// teamId = MongoDB _id of the existing team to register for this league
export interface RegisterLeagueTeamBody {
    teamId: string;
}

export interface RegisterTeamResponse {
    team: LeagueTeam;
    message: string;
}
export { CompetitionMatchStatus };

export const MATCH_STATUS_LABELS: Record<CompetitionMatchStatus, string> = {
    [CompetitionMatchStatus.SCHEDULED]: "Scheduled",
    [CompetitionMatchStatus.READY]: "Ready",
    [CompetitionMatchStatus.IN_PROGRESS]: "In Progress",
    [CompetitionMatchStatus.AWAITING_CONFIRMATION]: "Awaiting Confirmation",
    [CompetitionMatchStatus.DISPUTED]: "Disputed",
    [CompetitionMatchStatus.COMPLETED]: "Completed",
    [CompetitionMatchStatus.CANCELLED]: "Cancelled",
    [CompetitionMatchStatus.FORFEIT]: "Forfeit",
};

// Match participant
export interface MatchParticipant {
    participantId: string;
    participantType: string;
    displayName: string;
    score?: number;
    confirmedResult?: boolean;
    confirmedAt?: number;
}

// Score submission record
export interface MatchScoreSubmission {
    participantId: string;
    participant1Score: number;
    participant2Score: number;
    submittedAt: number;
    submittedBy: string;
}

// Match dispute
export interface MatchDispute {
    active: boolean;
    raisedBy: string;
    raisedAt: number;
    reason: string;
    evidence?: string[];
    resolvedBy?: string;
    resolvedAt?: number;
    resolution?: string;
}

// Match type for schedule display
export interface Match {
    id: string;
    competitionType: string;
    competitionId: string;
    participant1?: MatchParticipant;
    participant2?: MatchParticipant;
    status: CompetitionMatchStatus;
    round?: number;
    scheduledStartTime?: number;
    winnerId?: string;
    isDraw?: boolean;
    completedAt?: number;
    submissions?: MatchScoreSubmission[];
    dispute?: MatchDispute;
    createdAt: number;
    updatedAt: number;
}

// Schedule matchday with populated fixtures
export interface ScheduleMatchday extends LeagueMatchday {
    fixtures: Match[];
}

// Schedule response
export interface GetLeagueScheduleResponse {
    leagueId: string;
    matchdays: ScheduleMatchday[];
    currentMatchday?: number;
}

// Match detail response
export interface GetMatchResponse {
    match: Match;
}

// Submit score body — field names match backend swagger SubmitResultBody (homeScore/awayScore)
export interface SubmitScoreBody {
    homeScore: number;
    awayScore: number;
}

// Submit score response
export interface SubmitScoreResponse {
    match: Match;
    message: string;
}

// Confirm result response
export interface ConfirmResultResponse {
    match: Match;
    message: string;
}

// Dispute result body
export interface DisputeResultBody {
    reason: string;
    evidence?: string[];
}

// Dispute result response
export interface DisputeResultResponse {
    match: Match;
    message: string;
}
